import React, { useEffect, useState } from "react";
import { Mail, Eye, Send, Loader2, Calendar, Users } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";

const MONTHS = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
];

const now = new Date();
const defaultMonth = now.getMonth() === 0 ? 12 : now.getMonth();
const defaultYear = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();

/**
 * DigestPanel — admin tool for the monthly Foundation digest email.
 * Preview → review → optional dry-run → send.
 */
const DigestPanel = () => {
    const [year, setYear] = useState(defaultYear);
    const [month, setMonth] = useState(defaultMonth);
    const [previewHtml, setPreviewHtml] = useState("");
    const [dryRun, setDryRun] = useState(null);
    const [previewAsDonor, setPreviewAsDonor] = useState("");
    const [loading, setLoading] = useState(false);
    const [sending, setSending] = useState(false);
    const [runs, setRuns] = useState([]);
    const [loadingRuns, setLoadingRuns] = useState(true);

    const loadRuns = () =>
        api.get("/admin/digest/runs")
            .then((r) => setRuns(r.data || []))
            .catch(() => {})
            .finally(() => setLoadingRuns(false));

    useEffect(() => { loadRuns(); }, []);

    const preview = async () => {
        setLoading(true);
        setPreviewHtml("");
        setDryRun(null);
        try {
            const previewUrl = `${api.defaults.baseURL}/admin/digest/${year}/${month}/preview${previewAsDonor ? `?as_donor=${encodeURIComponent(previewAsDonor)}` : ""}`;
            const [dry, html] = await Promise.all([
                api.post(`/admin/digest/${year}/${month}/send?dry_run=true`),
                fetch(previewUrl, {
                    headers: { "Authorization": `Bearer ${localStorage.getItem("rdf_token") || ""}` },
                    credentials: "include",
                }).then((r) => r.text()),
            ]);
            setDryRun(dry.data);
            setPreviewHtml(html);
        } catch (err) {
            toast.error(err.response?.data?.detail || err.message || "Preview failed");
        } finally {
            setLoading(false);
        }
    };

    const send = async () => {
        if (!dryRun) {
            toast.error("Click 'Preview' first so you can see what will be sent.");
            return;
        }
        if (!window.confirm(`Send the ${MONTHS[month - 1]} ${year} digest to ${dryRun.recipients} subscribers? This cannot be undone.`)) return;
        setSending(true);
        try {
            const { data } = await api.post(`/admin/digest/${year}/${month}/send`);
            toast.success(`Digest sent — queued ${data.queued} emails`);
            await loadRuns();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Send failed");
        } finally {
            setSending(false);
        }
    };

    return (
        <div data-testid="admin-digest-panel">
            <div className="bg-white border border-black/5 rounded-3xl p-8 mb-8">
                <div className="flex items-start gap-3 mb-6">
                    <Mail className="w-5 h-5 text-[#E53935] flex-shrink-0 mt-1" strokeWidth={1.5} />
                    <div>
                        <h2 className="font-heading text-2xl text-[#1C252E] font-medium">Foundation digest</h2>
                        <p className="text-sm text-[#4A5568] mt-1.5 max-w-2xl">
                            One-click monthly recap email to every newsletter subscriber. Pulls the top P2P fundraiser, biggest payout, newest impact story, and total raised that month. Auto-fires on the 1st-3rd of each month via cron (idempotent).
                        </p>
                    </div>
                </div>

                <div className="flex flex-wrap gap-3 items-end mb-6">
                    <div>
                        <label htmlFor="digest-year-input" className="text-xs uppercase tracking-wider font-bold text-[#4A5568] block mb-2">Year</label>
                        <input
                            id="digest-year-input"
                            type="number"
                            min={2024}
                            max={now.getFullYear()}
                            value={year}
                            onChange={(e) => setYear(parseInt(e.target.value, 10) || defaultYear)}
                            data-testid="admin-digest-year-input"
                            className="w-24 rounded-xl border border-black/10 px-3 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]/40"
                        />
                    </div>
                    <div>
                        <label htmlFor="digest-month-select" className="text-xs uppercase tracking-wider font-bold text-[#4A5568] block mb-2">Month</label>
                        <select
                            id="digest-month-select"
                            value={month}
                            onChange={(e) => setMonth(parseInt(e.target.value, 10))}
                            data-testid="admin-digest-month-select"
                            className="rounded-xl border border-black/10 px-3 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]/40"
                        >
                            {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
                        </select>
                    </div>
                    <button
                        onClick={preview}
                        disabled={loading}
                        data-testid="admin-digest-preview-btn"
                        className="inline-flex items-center gap-2 bg-[#1C252E] hover:bg-black disabled:opacity-60 text-white rounded-full px-5 py-2.5 font-medium"
                    >
                        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" strokeWidth={1.5} />}
                        Preview
                    </button>
                    <input
                        type="email"
                        placeholder="Preview as donor (optional email)"
                        value={previewAsDonor}
                        onChange={(e) => setPreviewAsDonor(e.target.value)}
                        data-testid="admin-digest-as-donor-input"
                        aria-label="Preview as donor email"
                        className="rounded-xl border border-black/10 px-3 py-2.5 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#E53935]/40 min-w-[240px]"
                        title="Optional — type a real donor's email to see the personalized version they'd receive"
                    />
                    {dryRun && (
                        <button
                            onClick={send}
                            disabled={sending}
                            data-testid="admin-digest-send-btn"
                            className="inline-flex items-center gap-2 bg-[#E53935] hover:bg-[#D32F2F] disabled:opacity-60 text-white rounded-full px-5 py-2.5 font-medium"
                        >
                            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" strokeWidth={1.5} />}
                            Send to {dryRun.recipients} subscribers
                        </button>
                    )}
                </div>

                {dryRun && (
                    <div className="grid sm:grid-cols-5 gap-3 mb-4 text-xs" data-testid="admin-digest-stats">
                        <div className="bg-[#FDFBF7] rounded-xl px-4 py-3">
                            <p className="uppercase tracking-wider text-[#4A5568] font-bold">Subscribers</p>
                            <p className="font-heading text-xl text-[#1C252E] font-medium" data-testid="admin-digest-recipients-count">{dryRun.recipients}</p>
                        </div>
                        <div className="bg-[#F2D8D5] rounded-xl px-4 py-3">
                            <p className="uppercase tracking-wider text-[#E53935] font-bold">Personalized</p>
                            <p className="font-heading text-xl text-[#1C252E] font-medium" data-testid="admin-digest-personalized-count" title="Subscribers who also donated this month — they'll see a custom 'Your impact' strip">
                                {dryRun.personalized_count ?? 0}
                            </p>
                        </div>
                        <div className="bg-[#FDFBF7] rounded-xl px-4 py-3">
                            <p className="uppercase tracking-wider text-[#4A5568] font-bold">Raised</p>
                            <p className="font-heading text-xl text-[#1C252E] font-medium">${dryRun.payload?.total_raised?.toFixed(0) ?? 0}</p>
                        </div>
                        <div className="bg-[#FDFBF7] rounded-xl px-4 py-3">
                            <p className="uppercase tracking-wider text-[#4A5568] font-bold">Gifts</p>
                            <p className="font-heading text-xl text-[#1C252E] font-medium">{dryRun.payload?.gift_count ?? 0}</p>
                        </div>
                        <div className="bg-[#FDFBF7] rounded-xl px-4 py-3">
                            <p className="uppercase tracking-wider text-[#4A5568] font-bold">Cards</p>
                            <p className="font-heading text-xl text-[#1C252E] font-medium">
                                {[dryRun.payload?.top_fundraiser, dryRun.payload?.biggest_payout, dryRun.payload?.newest_story].filter(Boolean).length}
                            </p>
                        </div>
                    </div>
                )}

                {previewHtml && (
                    <div className="rounded-2xl overflow-hidden border border-black/5 bg-[#F4F1EA]" data-testid="admin-digest-preview-iframe-wrap">
                        <iframe
                            title="Digest preview"
                            srcDoc={previewHtml}
                            data-testid="admin-digest-preview-iframe"
                            className="w-full h-[640px] bg-white"
                            sandbox=""
                        />
                    </div>
                )}
            </div>

            {/* Audit log */}
            <div className="bg-white border border-black/5 rounded-3xl p-8" data-testid="admin-digest-runs">
                <div className="flex items-center gap-2 mb-4">
                    <Calendar className="w-4 h-4 text-[#4A5568]" strokeWidth={1.5} />
                    <h3 className="font-heading text-xl text-[#1C252E] font-medium">Send history</h3>
                </div>
                {loadingRuns ? (
                    <p className="text-[#4A5568] text-sm">Loading…</p>
                ) : runs.length === 0 ? (
                    <p className="text-[#4A5568] text-sm" data-testid="admin-digest-runs-empty">
                        No digests sent yet. The auto-cron will fire 1st-3rd of next month, or send manually above.
                    </p>
                ) : (
                    <div className="overflow-x-auto" data-testid="admin-digest-runs-list">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-black/10 text-left text-xs uppercase tracking-wider text-[#4A5568]">
                                    <th className="py-3 pr-4">Period</th>
                                    <th className="py-3 pr-4">Recipients</th>
                                    <th className="py-3 pr-4">Personalized</th>
                                    <th className="py-3 pr-4">Raised</th>
                                    <th className="py-3 pr-4">Gifts</th>
                                    <th className="py-3 pr-4">Source</th>
                                    <th className="py-3">When</th>
                                </tr>
                            </thead>
                            <tbody>
                                {runs.map((r) => (
                                    <tr key={r.id} className="border-b border-black/5" data-testid={`admin-digest-run-${r.id}`}>
                                        <td className="py-3 pr-4 font-medium text-[#1C252E]">{r.period_label}</td>
                                        <td className="py-3 pr-4 text-[#4A5568] inline-flex items-center gap-1.5"><Users className="w-3 h-3" strokeWidth={2} /> {r.queued} / {r.recipients_total}</td>
                                        <td className="py-3 pr-4 text-[#E53935] font-medium">{r.personalized_count ?? 0}</td>
                                        <td className="py-3 pr-4 text-[#1C252E] font-medium">${(r.total_raised || 0).toFixed(0)}</td>
                                        <td className="py-3 pr-4 text-[#4A5568]">{r.gift_count}</td>
                                        <td className="py-3 pr-4">
                                            <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${r.auto_fired ? "bg-blue-100 text-blue-700" : "bg-amber-100 text-amber-700"}`}>
                                                {r.auto_fired ? "Auto" : "Manual"}
                                            </span>
                                        </td>
                                        <td className="py-3 text-[#4A5568] text-xs">{r.sent_at ? new Date(r.sent_at).toLocaleString() : ""}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default DigestPanel;
